import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  BreadcrumbComponent,
  SearchInputComponent,
  SelectInputComponent,
} from "../../../../common/components";
import { getLeads } from "../../../../api/lead";
import { PaginationComponent } from "../../../../common/components";
import { useSetLoading } from "../../../../common/hooks/appearance";
import TooltipIcon from "../../../../assets/icons/tooltip-icon.svg";
import "../lead-assignment.scss";
import LeadsListTable from "./leads-list-table";
import { AlertComponent } from "../../../../common/components";
import { Link } from "react-router-dom";
import { routes } from "../../../../constants/routes";
import ConfirmDialog from "../../../../common/components/confirm-dialog/confirm-dialog";
import { leadApprove } from "../../../../api/lead";
import Stack from "@mui/material/Stack";
import Badge from "@mui/material/Badge";
import FilterIcon from "../../../../assets/icons/filter-icon.svg";
import _ from "lodash";
import { filterOptions } from "../../lead-filter/lead-filter.constant";
import LeadView from "../../lead-view/lead-view";
import { useResourcePermissions } from "../../../../common/hooks/resource-permissions";
import MODULES from "../../../../constants/modules";
import { useMediaQuery } from "@mui/material";

interface SortOption {
  sortDirection: string | null;
  sortBy: string | null;
}
const UnAssignedLeads = () => {
  const defaultBreadcrumbLink = [
    { label: "Lead Assignment", link: routes.UNASSIGNEDLEADS, handleClick: () => { } },
    { label: "Unassigned Leads", link: "#", handleClick: () => { } },
  ];

  const { canDelete, canRead, canWrite } = useResourcePermissions(
    MODULES.LEAD_ASSIGNMENT
  );
  const [crumbMappingOption, setCrumbMappingOption] = useState<any>(
    defaultBreadcrumbLink
  );
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedLeadsMap, setSelectedLeadsMap] = useState<{
    [page: number]: number[];
  }>({});
  const isAllSelected =
    data.length > 0 && selectedLeadsMap[currentPage]?.length === data.length;

  const isIndeterminate =
    data.length > 0 &&
    selectedLeadsMap[currentPage]?.length > 0 &&
    selectedLeadsMap[currentPage]?.length < data.length;
  const [loading, setLoading] = useState<boolean>(false);
  const [pageSize] = useState<number>(15);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortDirection: null,
    sortBy: null,
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [selectedCreateLeads, setSelectedCreateLeads] = useState<any>(null);
  const [selectedActions, setSelectedActions] = useState<any>(null);
  const [selectedAllLeads, setSelectedAllLeads] = useState<any>(null);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [selectedLeadsFilter, setSelectedLeadsFilters] = useState<number[]>([]);
  const [selectedLeadsFilterOptions, setSelectedLeadsFilterOptions] =
    useState<any[]>(filterOptions);
  const [searchText, setSearchText] = useState<string>("");
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const isSearchTriggered = useRef(false);
  const [viewOpen, setViewOpen] = useState<boolean>(false);
  const [selectedLeadId, setSelectedLeadId] = useState<any>(null);
  const [selectedLeadName, setSelectedLeadName] = useState<string>("");
   const isMobile = useMediaQuery("(max-width:768px)");
  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1); // reset to page 1 on search
      setSearchText(text);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    []
  );

  const resetSearch = () => {
    setSearchText("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
    handleSearch("");
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };
  const handleApproveLeads = async () => {
    try {
      const selectedLeadIds = selectedLeadsMap[currentPage] || [];
      if (selectedLeadIds.length === 0) {
        setAlertMessage("Please select at least one lead to approve.");
        setAlertType("warning");
        setAlertOpen(true);
        return;
      }

      const response = await leadApprove({ leadsIds: selectedLeadIds });
      const message =
        response?.data?.message ||
        (selectedLeadIds.length === 1
          ? "Lead has been approved successfully."
          : "Leads have been approved successfully.");

      setAlertMessage(message);
      setAlertType("success");
      setAlertOpen(true);
      setConfirmOpen(false);
      setSelectedLeadsMap((prev) => ({
        ...prev,
        [currentPage]: [],
      }));

      fetchLeads(currentPage); // Refresh list
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to approve leads."
      );
      setAlertType("error");
      setAlertOpen(true);
      setConfirmOpen(false);
    }
  };
  const noResultsTxt = useMemo(() => {
    if (loading) {
      return "Loading Leads...";
    }
    if (searchText) {
      return "No Leads found matching your search criteria.";
    }
    return "No Leads found";
  }, [loading, searchText]);

  const fetchLeads = async (
    page = 1,
    search: string = "",
    filters: any = {}
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      let params: any = {
        limit: pageSize,
        offset: offset,
        unassignedLeadsList: true,
      };
      if (sortOption?.sortBy) {
        params = { ...params, ...sortOption };
      }
      if (search) {
        params.searchText = search;
      }
      if (filters?.annualRevenue?.min) {
        params.leadValueMin = filters.annualRevenue.min;
      }
      if (filters?.annualRevenue?.max) {
        params.leadValueMax = filters.annualRevenue.max;
      }
      if (filters?.email) {
        params.email = filters.email;
      }
      if (filters?.city) {
        params.city = filters.city;
      }
      if (filters?.country) {
        params.country = filters.country;
      }
      if (filters?.fax) {
        params.fax = filters.fax;
      }
      if (filters?.firstName) {
        params.firstName = filters.firstName;
      }
      if (filters?.lastName) {
        params.lastName = filters.lastName;
      }
      if (filters?.company) {
        params.company = filters.company;
      }
      if (filters?.industry) {
        params.industry = filters.industry;
      }
      if (filters?.confidenceScore) {
        params.confidenceScore = filters.confidenceScore;
      }
      if (filters?.createdTimeStart && filters.createdTimeEnd) {
        // if (filters.createdTimeStart.isSame(filters.createdTimeEnd)) {
          params.createdTimeStart = filters.createdTimeStart.startOf("day");
          params.createdTimeEnd = filters.createdTimeEnd.endOf("day");
        // } else {
        //   params.createdTimeStart = filters.createdTimeStart;
        //   params.createdTimeEnd = filters.createdTimeEnd;
        // }
      }
      if (filters?.lastActivityTimeStart && filters.lastActivityTimeEnd) {
        // if (filters.lastActivityTimeStart.isSame(filters.lastActivityTimeEnd)) {
          params.lastActivityStart =
            filters.lastActivityTimeStart.startOf("day");
          params.lastActivityEnd = filters.lastActivityTimeEnd.endOf("day");
        // } else {
        //   params.lastActivityStart = filters.lastActivityTimeStart;
        //   params.lastActivityEnd = filters.lastActivityTimeEnd;
        // }
      }
      if (filters?.createdBy?.length > 0) {
        params.createdBy = filters.createdBy.map(Number);
      }
      if (filters?.leadOwner?.length > 0) {
        params.leadOwner = filters.leadOwner.map(Number);
      }
      if (filters?.assignedPartner?.length > 0) {
        params.assignedPartner = filters.assignedPartner.map(Number);
      }
      const response = await getLeads(params, "Lead Assignment");
      setData(response.data.data.leads);
      setTotalRecords(response.data.data.totalRecords);
      setTimeout(() => setLoading(false), 1000);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch campaigns"
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  useEffect(() => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    fetchLeads(pageToUse, searchText, activeFilters);
    isSearchTriggered.current = false;
  }, [currentPage, searchText, sortOption, activeFilters]);

  const handleBackArrowClick = (event:any) => {
    event.preventDefault();
    setCrumbMappingOption(defaultBreadcrumbLink);
    setViewOpen(false);
    setCurrentPage(1);
    fetchLeads();
  }

  const handleViewClick = async (leadId: number, leadName: string) => {
    setSelectedLeadId(leadId);
    setSelectedLeadName(leadName);
    setCrumbMappingOption([
      {
        label: "Lead Assignment",
        link: routes.UNASSIGNEDLEADS,
        handleClick: handleBackArrowClick
      },
      {
        label: "Unassigned Leads",
        link: routes.UNASSIGNEDLEADS,
        handleClick: handleBackArrowClick
      },
      { label: leadName, link: "" },
    ]);
    setViewOpen(true);
  };

  const handleLeadNameUpdate = (newName: string) => {
    setSelectedLeadName(newName);
    setCrumbMappingOption((prev: any) => {
      const updated = [...prev];
      updated[2] = { ...updated[2], label: newName };
      return updated;
    });
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  const isAnyLeadSelected = Object.values(selectedLeadsMap).some(
    (ids) => ids.length > 0
  );
  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive =
      activeFilters.createdTimeStart || activeFilters.createdTimeEnd;
    const runFilterActive =
      activeFilters.lastActivityTimeStart || activeFilters.lastActivityTimeEnd;

    const otherFilters = { ...activeFilters };
    delete otherFilters.createdTimeStart;
    delete otherFilters.createdTimeEnd;
    delete otherFilters.lastActivityTimeStart;
    delete otherFilters.lastActivityTimeEnd;

    const otherActiveFilters = Object.values(otherFilters).filter((val) => {
      if (val === undefined || val === "" || val === null) return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === "object") {
        return Object.values(val).some(
          (v) => v !== "" && v !== undefined && v !== null
        );
      }
      return true;
    }).length;

    if (dateFilterActive) {
      count += 1;
    }
    if (runFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [activeFilters]);

  return (
    <>
      <div className="leadlist-wrapper un-assigned-leads-wrapper">
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent breadcrumbLink={crumbMappingOption} />
        </div>
        <div style={{ display: !viewOpen ? "block" : "none" }}>
          <div className="un-assigned-leads-header">
            <div className="total-records-wrapper left-header">
              <div className="unassigned-list-search-wrapper">
                <SearchInputComponent
                  placeholder="Search by Lead name, Email, Lead owner etc."
                  onSearch={handleSearch}
                  isReset={isResetSearch}
                  ref={searchRef}
                />
              </div>
            </div>

            <div className="right-header">
              {/* Elements pushed to the right */}
              <div className="tooltip-text-wrapper">
                <img
                  src={TooltipIcon}
                  alt="tool-tip-icon"
                  className="tool-tip-icon"
                />
                <p className="tooltip-text">{`Only approved leads will be used in automatic lead assignment`}</p>
              </div>
               {isMobile ? (
                  <div className="mobile-actions-wrapper">
                    <div className="custom-btn-wrapper">
                      <button className="custom-btn all-approve-leads-btn">
                        <Link to={routes.APPROVEDLEADS} className="link">
                          All Approved Leads
                        </Link>
                      </button>
                    </div>

                    <div
                      className="lead-list-icon-filter"
                      onClick={() => setIsOpenFilter(!isOpenFilter)}
                      tabIndex={0}
                    >
                      <Stack
                        spacing={4}
                        direction="row"
                        sx={{ color: "action.active" }}
                      >
                        <Badge
                          color="secondary"
                          badgeContent={appliedFilterCount}
                          sx={{
                            '& .MuiBadge-badge': {
                              backgroundColor: '#887952',
                            },
                          }}
                        >
                          <img src={FilterIcon} alt="Filter Icon" />
                        </Badge>
                      </Stack>
                    </div>

                    <div className="custom-btn-wrapper">
                      <button
                        className="custom-btn approve-leads"
                        onClick={() => setConfirmOpen(true)}
                        disabled={!isAnyLeadSelected || !canWrite}
                      >
                        Approve Leads
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="custom-btn-wrapper">
                      <button className="custom-btn all-approve-leads-btn">
                        <Link to={routes.APPROVEDLEADS} className="link">
                          All Approved Leads
                        </Link>
                      </button>
                    </div>

                    <div
                      className="lead-list-icon-filter"
                      onClick={() => setIsOpenFilter(!isOpenFilter)}
                      tabIndex={0}
                    >
                      <Stack
                        spacing={4}
                        direction="row"
                        sx={{ color: "action.active" }}
                      >
                        <Badge
                          color="secondary"
                          badgeContent={appliedFilterCount}
                          sx={{
                            '& .MuiBadge-badge': {
                              backgroundColor: '#887952',
                            },
                          }}
                        >
                          <img src={FilterIcon} alt="Filter Icon" />
                        </Badge>
                      </Stack>
                    </div>

                    <div className="custom-btn-wrapper">
                      <button
                        className="custom-btn approve-leads"
                        onClick={() => setConfirmOpen(true)}
                        disabled={!isAnyLeadSelected || !canWrite}
                      >
                        Approve Leads
                      </button>
                    </div>
                  </>
                )}
            </div>
          </div>

          <LeadsListTable
            fieldName="un-assigned-leads-list"
            data={data}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            selectedLeadsMap={selectedLeadsMap}
            setSelectedLeadsMap={setSelectedLeadsMap}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            setSortOption={setSortOption}
            customClassName="un-assign-leads-list-table"
            isOpenFilter={isOpenFilter}
            setIsOpenFilter={setIsOpenFilter}
            selectedLeadsFilter={selectedLeadsFilter}
            setSelectedLeadsFilters={setSelectedLeadsFilters}
            selectedLeadsFilterOptions={selectedLeadsFilterOptions}
            setSelectedLeadsFilterOptions={setSelectedLeadsFilterOptions}
            setActiveFilters={setActiveFilters}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}
            setAlertOpen={setAlertOpen}
            noResultsTxt={noResultsTxt}
            viewOpen={viewOpen}
            setViewOpen={setViewOpen}
            handleViewClick={handleViewClick}
            activeFilters={activeFilters}
          />

          {totalRecords && totalRecords > 0 ? (
            <div className="leads-pagination-container">
              <PaginationComponent
                totalRecords={totalRecords}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          ) : (
            <></>
          )}
        </div>
        {!viewOpen ? (
          ""
        ) : (
          <LeadView
            setLoading={setLoading}
            selectedLeadId={selectedLeadId}
            onLeadNameUpdate={handleLeadNameUpdate}
          />
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Lead Approval"
        description={
          selectedLeadsMap[currentPage]?.length === 1
            ? "Are you sure you want to approve the selected lead?"
            : "Are you sure you want to approve the selected leads?"
        }
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleApproveLeads}
      />
      <AlertComponent
        message={alertMessage}
        open={alertOpen}
        type={alertType}
        onClose={() => {
          setAlertOpen(false);
        }}
      />
    </>
  );
};

export default UnAssignedLeads;
